import React from 'react';
// External dependencies - using lucide-react icons
import { Building2, Phone, LucideIcon } from 'lucide-react';

// Local imports
import { IStatusAction } from './userTypes';
import IvicosStrings from 'kits/language/stringKit';
import { useUserStatus } from 'shared-state/identity/hooks';
import { UserStatus, STATUS_COLORS } from 'shared-state/identity/types';
import { IC } from 'components/SVGIcon';
import { AwayStatus, OOOStatus, BusyStatus } from 'kits/IconKit2';
// import { awayStatusIcon, busyStatusIcon, onCallStatusIcon, outOfOfficeStatusIcon } from '../../UserFace/UserSelfStatus/icons';

// Icon constants
const ICON_SIZE = 16;
const ICON_COLOR = 'white';
const ICON_COLOR_ACTIVE = 'black';
const ICON_STROKE_WIDTH = 3;
const ACTIVE_BG_COLOR = '#9E9E9E'; // Grey for active/selected status

// Status configuration - defines all available statuses
type StatusIconComponent = React.ComponentType<{
    size?: number;
    color?: string;
    strokeWidth?: number;
    fill?: string;
}>;

interface StatusConfig {
    key: UserStatus;
    label: () => string;
    Icon: StatusIconComponent | LucideIcon;
    filled?: boolean;
}

const AwayStatusIcon: StatusIconComponent = () => (
    <IC variant="custom" size={28} iconColor="black">
        <AwayStatus />
    </IC>
);

// const AwayStatusIcon: StatusIconComponent = () => <img src={awayStatusIcon} alt="away status" width={28} height={28} />;
const BusyStatusIcon: StatusIconComponent = ({ size = 28, color = 'white' }) => (
    <IC variant="custom" size={size} iconColor={color}>
        <BusyStatus />
    </IC>
);
const OnCallStatusIcon: StatusIconComponent = ({ color = 'white' }) => <Phone color={color} size={22} strokeWidth={1.5} />;
const OutOfOfficeStatusIcon: StatusIconComponent = ({ color = 'white' }) => (
    <IC variant="custom" size={28} iconColor={color}>
        <OOOStatus />
    </IC>
);

const STATUS_CONFIG: StatusConfig[] = [
    { key: 'available', label: () => IvicosStrings.statusAtWork, Icon: Building2, filled: true },
    {
        key: 'away',
        label: () => IvicosStrings.statusAway,
        Icon: AwayStatusIcon
    },
    { key: 'busy', label: () => IvicosStrings.statusDoNotDisturb, Icon: BusyStatusIcon },
    { key: 'on-the-phone', label: () => IvicosStrings.statusOnThePhone, Icon: OnCallStatusIcon },
    { key: 'out-of-office', label: () => IvicosStrings.statusOutOfOffice, Icon: OutOfOfficeStatusIcon }
];

/**
 * Hook that provides status actions for the current user's profile hover.
 * Selecting a status applies a colored ring around the profile image.
 */
export const useStatusActions = (): {
    actions: IStatusAction[];
    isCurrentUser: boolean;
    currentStatus: UserStatus | undefined;
    currentStatusColor: string | undefined;
} => {
    const [currentStatus, setCurrentStatus] = useUserStatus();

    const handleStatusChange = (status: UserStatus) => {
        setCurrentStatus(status);
    };

    // Generate actions from config (no icon is "active" until user selects one)
    const actions: IStatusAction[] = STATUS_CONFIG.map(({ key, label, Icon, filled }) => {
        const isActive = currentStatus !== undefined && currentStatus === key;
        const statusColor = STATUS_COLORS[key];
        const isAtWorkAction = key === 'available';
        // At-work: icon varies by atWorkLocation (Office=Building2, Remote=Home, Other=MapPin)
        // const ResolvedIcon = isAtWorkAction ? AT_WORK_ICONS[atWorkLocation] : Icon;
        const isAvailable = isAtWorkAction && filled;
        // Active: grey background with colored border ring, black icon
        // Inactive: solid status color background, white icon
        const iconColor = isActive ? ICON_COLOR_ACTIVE : ICON_COLOR;
        const bgColor = isActive ? ACTIVE_BG_COLOR : statusColor;
        const borderColor = isActive ? statusColor : undefined; // Ring in status color when active

        return {
            key,
            title: isActive ? IvicosStrings.statusCurrent : label(),
            action: () => handleStatusChange(key),
            onRenderIcon: () => (
                <Icon
                    size={ICON_SIZE}
                    color={isAvailable && !isActive ? statusColor : isAvailable && isActive ? ACTIVE_BG_COLOR : iconColor}
                    strokeWidth={ICON_STROKE_WIDTH}
                    {...(filled && { fill: !isActive ? statusColor : ACTIVE_BG_COLOR })}
                />
            ),
            color: bgColor,
            borderColor,
            isActive,
            isAvailable: isAtWorkAction, // Mark At work action to open location menu instead of immediate action
            isAway: key === 'away', // Mark Away action to open menu instead of immediate action
            isBusy: key === 'busy', // Mark Do Not Disturb action to open Focus Time modal instead of immediate action
            isOnThePhone: key === 'on-the-phone', // Mark On the phone action to open On Phone modal instead of immediate action
            isOutOfOffice: key === 'out-of-office' // Mark Out of office action to open OOO modal instead of immediate action
        };
    });

    return {
        actions,
        isCurrentUser: true,
        currentStatus,
        currentStatusColor: currentStatus !== undefined ? STATUS_COLORS[currentStatus] : undefined
    };
};
